import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CurrencyDollarIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/auth';
import api from '../services/api';
import clsx from 'clsx';

interface Commission {
  id: string;
  type: 'CPA' | 'RevShare' | 'Hybrid';
  amount: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  playerId?: string;
  playerName?: string;
  description?: string;
  createdAt: string;
  approvedAt?: string;
  paidAt?: string;
}

interface CommissionSummary {
  totalEarned: number;
  pendingAmount: number;
  approvedAmount: number;
  paidAmount: number;
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function Commissions() {
  const { affiliate } = useAuthStore();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const { data: commissions, isLoading } = useQuery({
    queryKey: ['commissions', affiliate?.id, statusFilter, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('affiliateId', affiliate?.id || '');
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);

      const response = await api.get(`/commissions/history?${params.toString()}`);
      return response.data.data as Commission[];
    },
    enabled: !!affiliate?.id,
  });

  const { data: summary } = useQuery({
    queryKey: ['commission-summary', affiliate?.id],
    queryFn: async () => {
      const response = await api.get(`/commissions/summary/${affiliate?.id}`);
      return response.data.data as CommissionSummary;
    },
    enabled: !!affiliate?.id,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleExport = () => {
    // TODO: Implement CSV export
    alert('Export functionality coming soon!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Commissions</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            Track your earnings and commission history
          </p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100">
              <CurrencyDollarIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-[var(--text-muted)]">Total Earned</p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {formatCurrency(summary?.totalEarned || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-100">
              <CurrencyDollarIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-[var(--text-muted)]">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(summary?.pendingAmount || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
              <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-[var(--text-muted)]">Approved</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(summary?.approvedAmount || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-[var(--text-muted)]">Paid Out</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(summary?.paidAmount || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-[var(--text-muted)]" />
            <span className="text-sm font-medium text-[var(--text-secondary)]">Filters:</span>
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-[var(--border-color)] py-1.5 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-md border border-[var(--border-color)] py-1.5 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">All Types</option>
              <option value="CPA">CPA</option>
              <option value="RevShare">Revenue Share</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>
        </div>
      </div>

      {/* Commissions Table */}
      <div className="bg-[var(--bg-secondary)] rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-[var(--text-muted)]">Loading commissions...</p>
          </div>
        ) : !commissions?.length ? (
          <div className="p-8 text-center">
            <CurrencyDollarIcon className="mx-auto h-12 w-12 text-[var(--text-muted)]" />
            <h3 className="mt-2 text-sm font-medium text-[var(--text-primary)]">No commissions yet</h3>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Start promoting to earn your first commission!
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[var(--bg-secondary)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[var(--bg-secondary)] divide-y divide-gray-200">
                {commissions.map((commission) => (
                  <tr key={commission.id} className="hover:bg-[var(--bg-secondary)]">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[var(--text-primary)]">{formatDate(commission.createdAt)}</div>
                      {commission.paidAt && (
                        <div className="text-xs text-[var(--text-muted)]">
                          Paid: {formatDate(commission.paidAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center rounded-md bg-[var(--bg-tertiary)] px-2 py-1 text-xs font-medium text-[var(--text-primary)]">
                        {commission.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-[var(--text-primary)]">
                        {commission.description || 'Commission earned'}
                      </div>
                      {commission.playerName && (
                        <div className="text-xs text-[var(--text-muted)]">
                          Player: {commission.playerName}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">
                        {formatCurrency(commission.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={clsx(
                          'inline-flex rounded-full px-2 py-1 text-xs font-semibold capitalize',
                          statusColors[commission.status]
                        )}
                      >
                        {commission.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
